import { useEffect, useRef, useState } from 'react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputBase from '@mui/material/InputBase'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled } from '@mui/material/styles'
import { useAtom, useAtomValue } from 'jotai'
import { authenticationState, useTranslate } from '@repo/unicity'
import { checkoutReferralInfoAtom } from '@/core/checkout/checkout.store'
import { pendingReferralErrorAtom } from './referral.store'
import { useApplyReferral } from './useApplyReferral'
import ArrowRightIcon from './assets/arrow-long-right.svg?react'

const REFERRAL_BAR_BG = '#153862'

const ReferralBar = () => {
    const { translate } = useTranslate()
    const authentication = useAtomValue(authenticationState)
    const isAuthenticated = Boolean(authentication.token && authentication.baId)

    const referralInfo = useAtomValue(checkoutReferralInfoAtom)
    const applyReferral = useApplyReferral()

    const [refCode, setRefCode] = useState('')
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [pendingError, setPendingError] = useAtom(pendingReferralErrorAtom)

    const isProcessingRef = useRef(false)

    // Show the same invalid-code error for a code that came from a deep link
    useEffect(() => {
        if (!pendingError) return
        setRefCode(pendingError)
        setErrorMessage(translate('referral_code_invalid').replace('{{referral_code}}', pendingError))
        setPendingError('')
    }, [pendingError, setPendingError, translate])

    if (isAuthenticated) return null

    const handleSubmit = async (code?: string) => {
        const targetCode = code || refCode
        if (!targetCode.trim() || isProcessingRef.current) return

        isProcessingRef.current = true
        setLoading(true)
        setErrorMessage('')

        try {
            const accepted = await applyReferral(targetCode.trim())
            if (!accepted) {
                setErrorMessage(translate('referral_code_invalid').replace('{{referral_code}}', targetCode.trim()))
            }
        } catch {
            setErrorMessage(translate('referral_code_invalid').replace('{{referral_code}}', targetCode.trim()))
        } finally {
            setLoading(false)
            isProcessingRef.current = false
        }
    }

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handleSubmit()
    }

    return (
        <>
            <Stack bgcolor={REFERRAL_BAR_BG} py="18px" data-testid="referral-bar-stack">
                <Box color="#ffffff" display="flex" justifyContent="center" textAlign="center" px="20px" data-testid="referral-bar-box">
                    {!referralInfo ? (
                        <ReferralInput
                            refCode={refCode}
                            setRefCode={setRefCode}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                            onSubmit={handleFormSubmit}
                            onClickSubmit={() => handleSubmit()}
                            data-testid="referral-bar-referral-input-handle-form-submit"
                        />
                    ) : (
                        <Stack direction="row" alignItems="center" data-testid="referral-bar-stack-typography-referred-by">
                            <Typography variant="caption" fontSize="14px" color="#ffffff" data-testid="referral-bar-typography-referred-by">
                                {translate('referred_by')}{' '}
                                <Typography
                                    component="span"
                                    variant="caption"
                                    fontSize="14px"
                                    fontWeight={700}
                                    color="#ffffff"
                                    data-testid="referral-bar-typography-1">
                                    {referralInfo.displayName}
                                </Typography>
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>
            <Backdrop sx={{ color: '#fff', zIndex: t => t.zIndex.drawer + 1 }} open={loading} data-testid="referral-bar-backdrop">
                <CircularProgress color="inherit" data-testid="referral-bar-circular-progress" />
            </Backdrop>
        </>
    )
}

const ReferralInput = ({
    refCode,
    setRefCode,
    errorMessage,
    setErrorMessage,
    onSubmit,
    onClickSubmit
}: {
    refCode: string
    setRefCode: (v: string) => void
    errorMessage: string
    setErrorMessage: (v: string) => void
    onSubmit: (e: React.FormEvent) => void
    onClickSubmit: () => void
}) => {
    const { translate } = useTranslate()
    const isMobile = useMediaQuery('(max-width:834px)')
    const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null)

    return (
        <Stack direction="row" alignItems="center" gap="24px" data-testid="referral-bar-stack-form-on-submit-1">
            <Stack alignItems="flex-start" data-testid="referral-bar-stack-form-on-submit-2">
                <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column' }} data-testid="referral-bar-form-on-submit">
                    <StyledInput
                        autoComplete="off"
                        name="refCode"
                        placeholder={translate('enter_your_referal_code')}
                        value={refCode}
                        onChange={e => {
                            setRefCode(e.target.value)
                            setErrorMessage('')
                        }}
                        hasError={Boolean(errorMessage)}
                        endAdornment={
                            <InputAdornment position="end" data-testid="referral-bar-input-adornment">
                                <ArrowRightIcon
                                    style={{ cursor: 'pointer' }}
                                    onClick={onClickSubmit}
                                    data-testid="referral-bar-arrow-right-icon-on-click-submit"
                                />
                            </InputAdornment>
                        }
                        data-testid="referral-bar-styled-input-ref-code"
                    />
                </form>
                {errorMessage && (
                    <Typography
                        variant="body2"
                        mt="8px"
                        fontWeight={400}
                        fontSize="12px"
                        color="#FFBF3A"
                        textAlign="left"
                        data-testid="referral-bar-typography-2">
                        {errorMessage}
                    </Typography>
                )}
            </Stack>
            <Box display="flex" height="36px" alignItems="center" alignSelf="flex-start" data-testid="referral-bar-box-icon-button">
                <IconButton sx={{ p: 0 }} onClick={e => setInfoAnchor(infoAnchor ? null : e.currentTarget)} data-testid="referral-bar-icon-button">
                    <InfoSvg data-testid="referral-bar-info-svg" />
                </IconButton>

                <Popper
                    open={Boolean(infoAnchor)}
                    anchorEl={infoAnchor}
                    placement="bottom"
                    transition
                    sx={{ zIndex: 1400, borderRadius: '8px', px: { xs: '1rem', sm: 0 }, py: '20px' }}
                    data-testid="referral-bar-popper">
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350} data-testid="referral-bar-fade">
                            <Paper
                                sx={{
                                    width: isMobile ? 'initial' : '307px',
                                    p: '20px 20px 30px 20px',
                                    bgcolor: isMobile ? REFERRAL_BAR_BG : '#fff',
                                    borderRadius: isMobile ? 0 : '8px'
                                }}
                                data-testid="referral-bar-paper-close-svg">
                                <Stack data-testid="referral-bar-stack-close-svg">
                                    <Box display="flex" justifyContent="flex-end" data-testid="referral-bar-box-close-svg">
                                        <CloseSvg
                                            color={isMobile ? '#fff' : '#153862'}
                                            onClick={() => setInfoAnchor(null)}
                                            data-testid="referral-bar-close-svg"
                                        />
                                    </Box>
                                    <Typography
                                        fontWeight={600}
                                        fontSize="16px"
                                        mb="16px"
                                        color={isMobile ? '#fff' : '#153862'}
                                        data-testid="referral-bar-typography-what-is-referral-id">
                                        {translate('what_is_member_code')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontSize="13px"
                                        lineHeight="16px"
                                        color={isMobile ? '#fff' : '#003764'}
                                        data-testid="referral-bar-typography-referral-id-description">
                                        {translate('member_invite_code_caption')}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Fade>
                    )}
                </Popper>
            </Box>
        </Stack>
    )
}

const StyledInput = styled(InputBase, {
    shouldForwardProp: prop => prop !== 'hasError'
})<{ hasError?: boolean }>(({ hasError }) => ({
    width: '287px',
    height: '36px',
    opacity: hasError ? 1 : 0.5,
    color: '#FFFFFF',
    borderRadius: '6px',
    fontFamily: "'Inter', 'Prompt', sans-serif",
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '16px',
    padding: '10px 16px',
    border: hasError ? '1px solid #FFBF3A' : '1px solid #ffffff',
    '& input': {
        fontSize: '12px',
        '&::placeholder': {
            color: '#ffffff',
            opacity: 1
        }
    }
}))

const InfoSvg = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" data-testid="referral-bar-svg">
        <circle cx="10" cy="10" r="9.25" stroke="white" strokeWidth="1.5" data-testid="referral-bar-circle" />
        <text x="10" y="15" textAnchor="middle" fill="white" fontSize="13" fontFamily="Inter" fontWeight="600" data-testid="referral-bar-text-i">
            i
        </text>
    </svg>
)

const CloseSvg = ({ color, onClick }: { color: string; onClick: () => void }) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ cursor: 'pointer' }}
        onClick={onClick}
        data-testid="referral-bar-svg-on-click">
        <path d="M12 4L4 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" data-testid="referral-bar-path-1" />
        <path d="M4 4L12 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" data-testid="referral-bar-path-2" />
    </svg>
)

export default ReferralBar
